/**
 * Idle session timeout.
 *
 * Watches the document for user activity (mouse / keyboard / touch /
 * scroll). After `idleMs` of inactivity it fires `onIdle` so the
 * caller can show a "still here?" modal. After `idleMs + graceMs`
 * with no further activity it fires `onTimeout` so the caller can log
 * the user out.
 *
 * The clock is reset on any activity event so the modal closes itself
 * if the operator nudges the trackpad. To avoid pegging the CPU on
 * mousemove, activity coalesces through a 250 ms throttle.
 *
 * Cleanup is automatic: listeners + timers detach when the consumer
 * unmounts. The hook is a no-op when `enabled === false` so the
 * AuthedLayout can disable it on /login pages.
 */
import { useCallback, useEffect, useRef } from 'react';

export interface IdleTimeoutOptions {
  /** Quiet duration before `onIdle` fires. Default 30 min. */
  idleMs?: number;
  /** Extra duration after `onIdle` before `onTimeout` fires. Default 60 s. */
  graceMs?: number;
  /** Fires once when the operator has been idle for `idleMs`. */
  onIdle: () => void;
  /** Fires once when the operator has been idle for `idleMs + graceMs`. */
  onTimeout: () => void;
  /** Toggle: false unsubscribes everything (e.g. on /login). */
  enabled?: boolean;
}

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'wheel',
  'scroll',
] as const;
const ACTIVITY_THROTTLE_MS = 250;

export function useIdleTimeout({
  idleMs = 30 * 60 * 1000,
  graceMs = 60 * 1000,
  onIdle,
  onTimeout,
  enabled = true,
}: IdleTimeoutOptions): { reset: () => void } {
  // Stash the callbacks in refs so timer-fires use the latest closure
  // without us having to re-attach listeners every render.
  const onIdleRef = useRef(onIdle);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const idleTimer = useRef<number | null>(null);
  const timeoutTimer = useRef<number | null>(null);
  const lastActivity = useRef<number>(Date.now());
  const idleFired = useRef<boolean>(false);

  const clearTimers = useCallback(() => {
    if (idleTimer.current !== null) {
      window.clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
    if (timeoutTimer.current !== null) {
      window.clearTimeout(timeoutTimer.current);
      timeoutTimer.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    idleFired.current = false;
    if (!enabled) return;
    idleTimer.current = window.setTimeout(() => {
      idleFired.current = true;
      onIdleRef.current();
      timeoutTimer.current = window.setTimeout(() => {
        onTimeoutRef.current();
      }, graceMs);
    }, idleMs);
  }, [clearTimers, enabled, graceMs, idleMs]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }
    reset();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity.current < ACTIVITY_THROTTLE_MS) return;
      lastActivity.current = now;
      // Only reset the clock if the idle modal hasn't already opened —
      // once it's open, the operator's *explicit* "Stay signed in"
      // click owns the reset, otherwise a stray mousemove during the
      // grace window would silently dismiss the warning.
      if (!idleFired.current) reset();
    };
    for (const evt of ACTIVITY_EVENTS) {
      document.addEventListener(evt, handleActivity, { passive: true });
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        document.removeEventListener(evt, handleActivity);
      }
      clearTimers();
    };
  }, [enabled, reset, clearTimers]);

  return { reset };
}
